/**
 * Écosystème produit Shape — sections/shape-ecosystem.liquid
 * - Relie les points de la photo et les cartes accessoires : survoler (ou focus clavier) l'un
 *   met l'autre en avant, le reste passe en retrait
 * - Bouton « Ajouter au panier » des cartes : ajout sans rechargement, mise à jour du panier
 *   Horizon (tiroir, compteur) via CartAddEvent — même mécanique que assets/shape-collection.js
 * - Apparition en cascade quand le container entre à l'écran
 */
import { CartAddEvent } from '@theme/events';

if (!customElements.get('shape-ecosystem')) {
  class ShapeEcosystem extends HTMLElement {
    connectedCallback() {
      this.items = [...this.querySelectorAll('[data-eco-index]')];

      this.items.forEach((item) => {
        const index = item.getAttribute('data-eco-index');
        item.addEventListener('pointerenter', () => this.activate(index));
        item.addEventListener('pointerleave', () => this.activate(null));
        item.addEventListener('focusin', () => this.activate(index));
        item.addEventListener('focusout', () => this.activate(null));
      });

      this.addEventListener('submit', (event) => this.addToCart(event));

      this.reveal();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }

    /** @param {string | null} index */
    activate(index) {
      if (index === null) {
        this.removeAttribute('data-active');
      } else {
        this.setAttribute('data-active', index);
      }
      this.items.forEach((item) => {
        item.classList.toggle('is-active', index !== null && item.getAttribute('data-eco-index') === index);
      });
    }

    /** @param {SubmitEvent} event */
    async addToCart(event) {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.hasAttribute('data-eco-add')) return;
      event.preventDefault();

      const button = form.querySelector('button[type="submit"]');
      if (!button || button.classList.contains('is-loading')) return;
      const initialLabel = button.getAttribute('aria-label');
      button.classList.add('is-loading');

      const formData = new FormData(form);
      // Sections du panier à re-rendre (tiroir panier d'Horizon)
      const sectionIds = [...document.querySelectorAll('cart-items-component')]
        .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : ''))
        .filter(Boolean);
      if (sectionIds.length) formData.append('sections', sectionIds.join(','));

      try {
        const response = await fetch(window.Theme?.routes?.cart_add_url ?? '/cart/add', {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: formData,
        });
        const data = await response.json();
        if (!response.ok || data.status) throw new Error(data.description || data.message);

        button.classList.remove('is-loading');
        button.classList.add('is-added');
        button.setAttribute('aria-label', button.dataset.labelAdded ?? initialLabel ?? '');

        form.dispatchEvent(
          new CartAddEvent({}, String(formData.get('id')), {
            source: 'product-form-component',
            itemCount: Number(formData.get('quantity')) || 1,
            productId: formData.get('product-id')?.toString(),
            sections: data.sections,
          })
        );
      } catch (error) {
        // En cas d'erreur (stock insuffisant…), on revient à l'envoi classique du formulaire
        console.error(error);
        button.classList.remove('is-loading');
        form.removeAttribute('data-eco-add');
        form.submit();
        return;
      }

      window.setTimeout(() => {
        button.classList.remove('is-added');
        if (initialLabel) button.setAttribute('aria-label', initialLabel);
      }, 2200);
    }

    reveal() {
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      // Éditeur de thème : pas d'animation, la section doit rester visible pendant les réglages
      if (reduced || window.Shopify?.designMode || !('IntersectionObserver' in window)) return;

      this.classList.add('eco-ready');
      this.observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.observer.disconnect();
          this.classList.add('is-visible');
          // Fin de la cascade : les transitions de survol reprennent leur durée normale
          window.setTimeout(() => this.classList.add('is-settled'), 1600);
        },
        { threshold: 0.1 }
      );
      this.observer.observe(this);
    }
  }

  customElements.define('shape-ecosystem', ShapeEcosystem);
}
